import { IconCheck, IconChevronDown, IconLogout } from "@tabler/icons-react";
import { SignOutButton } from "@clerk/react";
import { useSelfQuery } from "@/lib/queries/user-queries";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import {
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenu,
} from "./DropdownMenu";
import { useMemo, useRef } from "react";
import Avatar from "./Avatar";
import { cn } from "@/lib/utils";

type Props = {
  classNameTrigger: string;
  isUserVisible: boolean;
  teamExternalId: string;
  onTeamSwitch: (teamExternalId: string) => void;
  signOutRedirectUrl?: string;
};

export function TeamSwitcher({
  classNameTrigger,
  isUserVisible,
  teamExternalId,
  onTeamSwitch,
  signOutRedirectUrl,
}: Props) {
  const { data } = useSelfQuery();
  const pointerDownRef = useRef(false);
  // const isSettingsPage = useMatch(paths.team(teamExternalId!));

  const membership = useMemo(() => {
    return data?.teamMemberships.find(
      (m) => m.teamExternalId === teamExternalId,
    );
  }, [data, teamExternalId]);

  const sortedTeamMemberships = useMemo(() => {
    if (!data) {
      return [];
    }

    return [...data.teamMemberships].sort((a, b) => {
      return a.teamName.localeCompare(b.teamName);
    });
  }, [data]);

  if (!data || !membership) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex cursor-pointer items-center transition-colors focus-visible:outline-tertiary-400",
            classNameTrigger,
          )}
          onPointerDown={() => {
            pointerDownRef.current = true;
          }}
        >
          <Avatar text={membership.teamName.slice(0, 1)} />
          <div className="mx-2.5 flex min-w-0 flex-1 flex-col text-left">
            <p className="line-clamp-1 text-sm font-semibold">
              {membership.teamName}
            </p>
            {isUserVisible ? (
              <p className="line-clamp-1 truncate text-xs text-text-secondary">
                {data.contactEmail}
              </p>
            ) : null}
          </div>
          <IconChevronDown className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-72"
        side="top"
        collisionPadding={20}
        onPointerDownOutside={() => {
          pointerDownRef.current = true;
        }}
        onCloseAutoFocus={(e) => {
          if (pointerDownRef.current) {
            e.preventDefault();
            pointerDownRef.current = false;
          }
        }}
      >
        {sortedTeamMemberships.map((membership, index) => (
          <DropdownMenuItem
            key={`${membership.teamExternalId}-${index}`}
            onPointerDown={() => {
              pointerDownRef.current = true;
            }}
            onClick={() => {
              // Only log if actually switching teams
              if (membership.teamExternalId !== teamExternalId) {
                AnalyticsApi.logTeamSwitched(membership.teamExternalId);
              }

              onTeamSwitch(membership.teamExternalId);
            }}
          >
            <div className="flex w-full items-center gap-2">
              <Avatar text={membership.teamName.slice(0, 1)} size="small" />
              <p className="line-clamp-1 flex-1 text-sm font-medium">
                {membership.teamName}
              </p>
              {membership.teamExternalId === teamExternalId ? (
                <IconCheck className="h-4 w-4" />
              ) : null}
            </div>
          </DropdownMenuItem>
        ))}
        {isUserVisible ? (
          <>
            <DropdownMenuSeparator />
            <SignOutButton redirectUrl={signOutRedirectUrl}>
              <DropdownMenuItem
                className="flex items-center"
                onPointerDown={() => {
                  pointerDownRef.current = true;
                }}
                onClick={() => {
                  // Log logout before the signout happens
                  AnalyticsApi.logUserLogout(teamExternalId);
                }}
              >
                <div className="flex h-6 w-6 items-center justify-center">
                  <IconLogout className="h-6 w-6" />
                </div>
                <p className="text-sm">Sign out</p>
              </DropdownMenuItem>
            </SignOutButton>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
